/**
 * On the patient page: when they are due, tracing attempts since their last visit, and a form to
 * record a phone call or home visit.
 */
import {
  type Patient,
  type PatientRecord,
  type Programme,
  TRACING_OUTCOMES,
  type TracingMethod,
  type TracingOutcome,
  addDays,
  followupStatus,
  tracingResources,
} from '@assist/core';
import { useState } from 'preact/hooks';
import { app, clock } from '../controller.js';
import { eth } from '../dates.js';
import { t } from '../i18n.js';

const METHODS: Array<[TracingMethod, string]> = [
  ['phone', t.methodPhone],
  ['home_visit', t.methodHome],
  ['community', t.methodCommunity],
];

export function FollowupCard(props: {
  patient: Patient;
  record: PatientRecord;
  programmes: Programme[];
  onSaved: () => void;
}) {
  const today = clock.today();
  const statuses = props.programmes
    .map((p) => followupStatus(props.record, p, today))
    .filter((s) => !!s);
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<TracingMethod>('phone');
  const [outcome, setOutcome] = useState<TracingOutcome | ''>('');
  const [days, setDays] = useState('7');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!statuses.length) return null;
  // the same order as the follow-up lists: lost, overdue, due, promised, upcoming
  const order = ['lost', 'overdue', 'due', 'promised', 'upcoming'];
  statuses.sort(
    (a, b) => order.indexOf(a.state) - order.indexOf(b.state) || b.daysLate - a.daysLate,
  );
  const s = statuses[0];
  if (!s) return null;
  const nameOf = (code: string) => props.programmes.find((p) => p.code === code)?.display ?? code;
  const line = (x: (typeof statuses)[number]) => (
    <>
      {x.state === 'lost' ? (
        <strong class="late">{t.tabLost}</strong>
      ) : x.daysLate > 0 ? (
        <strong class="late">{t.daysLate(x.daysLate)}</strong>
      ) : null}{' '}
      {t.nextDue(eth(x.dueDate))}{' '}
      {x.dueDateSet ? null : <span class="muted">{t.defaultInterval}</span>}
    </>
  );
  const n = Number(days);
  const returnDate =
    outcome === 'will_return' && Number.isInteger(n) && n > 0 ? addDays(today, n) : null;

  const save = async () => {
    if (!outcome || !app.session) return;
    setBusy(true);
    setErr(null);
    try {
      for (const r of tracingResources({
        patient: props.patient,
        record: props.record,
        today,
        nowIso: clock.now(),
        method,
        outcome,
        returnDate,
        note,
      }))
        await app.session.save(r);
      app.saved();
      setOpen(false);
      setOutcome('');
      setNote('');
      props.onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="card" data-card="followup">
      <h3>{t.followupCard}</h3>
      {statuses.length === 1 ? (
        <p>{line(s)}</p>
      ) : (
        <ul class="plain">
          {statuses.map((x) => (
            <li key={x.programme} data-programme={x.programme}>
              <strong>{nameOf(x.programme)}</strong>: {line(x)}
            </li>
          ))}
        </ul>
      )}
      {s.state === 'promised' && s.pausedUntil ? <p>{t.comesOn(eth(s.pausedUntil))}</p> : null}
      {s.attempts.length ? (
        <>
          <h3 class="small-h">{t.attemptsTitle}</h3>
          <ul class="plain">
            {s.attempts.map((x) => (
              <li key={x.id}>
                {eth(x.date)} · {METHODS.find((m) => m[0] === x.method)?.[1] ?? ''} ·{' '}
                <strong>{t.outcomes[x.outcome ?? ''] ?? '—'}</strong>
                {x.note ? <div class="muted">{x.note}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}>
          {t.recordAttempt}
        </button>
      ) : (
        <div class="attempt-form">
          <div class="field">
            <span class="label">{t.method}</span>
            <fieldset class="tri wrapbtn" aria-label={t.method}>
              {METHODS.map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  class={method === k ? 'on' : ''}
                  aria-pressed={method === k}
                  onClick={() => setMethod(k)}
                >
                  {label}
                </button>
              ))}
            </fieldset>
          </div>
          <div class="field">
            <label for="outcome">{t.outcome}</label>
            <select
              id="outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.currentTarget.value as TracingOutcome)}
            >
              <option value="">—</option>
              {TRACING_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {t.outcomes[o]}
                </option>
              ))}
            </select>
          </div>
          {outcome === 'will_return' ? (
            <div class="field">
              <label for="ret-days">{t.returnInDays}</label>
              <div class="num">
                <input
                  id="ret-days"
                  inputMode="numeric"
                  value={days}
                  onInput={(e) => setDays(e.currentTarget.value)}
                />
                {returnDate ? <span class="unit">{t.returnOn(eth(returnDate))}</span> : null}
              </div>
            </div>
          ) : null}
          {outcome === 'died' ? <div class="alert">{t.diedWarning}</div> : null}
          {outcome === 'transferred' ? <div class="alert">{t.transferWarning}</div> : null}
          <div class="field">
            <label for="att-note">{t.note}</label>
            <input id="att-note" value={note} onInput={(e) => setNote(e.currentTarget.value)} />
          </div>
          {err ? <div class="alert">{err}</div> : null}
          <div class="actions">
            <button type="button" class="ghost" onClick={() => setOpen(false)}>
              {t.cancel}
            </button>
            <button
              type="button"
              class="primary"
              disabled={!outcome || busy}
              onClick={() => void save()}
            >
              {busy ? t.saving : t.saveAttempt}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
